import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { ShieldCheck, Eye, ArrowRight } from "@phosphor-icons/react";

const PAGE_SIZE = 50;

const formatViewedAt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function PeekAuditLog() {
  const [params, setParams] = useSearchParams();
  const auctionId = params.get("auction_id") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchPage = useCallback(async (signal) => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/peek-log", {
        params: {
          page,
          page_size: PAGE_SIZE,
          ...(auctionId ? { auction_id: auctionId } : {}),
        },
        signal,
      });
      setItems(data.items || []);
      setTotal(data.total ?? 0);
      setTotalPages(data.total_pages ?? 1);
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      throw e;
    } finally {
      setLoading(false);
    }
  }, [page, auctionId]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchPage(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchPage]);

  const clearFilter = () => {
    const next = new URLSearchParams(params);
    next.delete("auction_id");
    setParams(next, { replace: true });
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="peek-audit-log-page">
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <div className="border-b border-black pb-6 mb-8">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-3">
            <ShieldCheck size={12} weight="bold" /> Super Admin · Accountability
          </div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tightest leading-none">
            Peek <span className="klein-blue">Audit</span> Log
          </h1>
          <p className="text-sm text-neutral-600 mt-3 max-w-2xl leading-relaxed">
            Every time a super-admin pulls the raw, identified bids for an auction it&apos;s recorded here.
            Use this log to verify that the blind-auction invariant is only being broken under accountable circumstances.
          </p>
          {auctionId && (
            <div className="mt-4 flex items-center gap-3 text-xs" data-testid="peek-log-filter-active">
              <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Filtered by auction</span>
              <span className="font-mono-plex border border-black/20 px-2 py-0.5">{auctionId}</span>
              <button
                onClick={clearFilter}
                className="text-xs uppercase tracking-[0.2em] font-semibold underline underline-offset-4"
                data-testid="peek-log-clear-filter"
              >
                clear
              </button>
            </div>
          )}
        </div>

        <div className="flex items-baseline justify-between border-b border-black pb-3 mb-0">
          <h2 className="font-display font-extrabold text-2xl tracking-tightest">Recent peek events</h2>
          <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500" data-testid="peek-log-total">
            {total} total
          </span>
        </div>

        {loading ? (
          <div className="py-10 text-xs tracking-[0.3em] uppercase text-neutral-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-black/15 p-10 text-center mt-4" data-testid="peek-log-empty">
            <div className="text-sm text-neutral-600">
              No peek events recorded yet. The log starts populating the moment any super-admin clicks &quot;Peek (super-admin)&quot; on an auction.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-black/10 border-b border-black/10" data-testid="peek-log-list">
            {items.map((e) => (
              <div
                key={e.id}
                className="grid grid-cols-12 gap-4 py-5 items-center px-2"
                data-testid={`peek-log-row-${e.id}`}
              >
                <div className="col-span-12 md:col-span-3">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">When</div>
                  <div className="font-mono-plex text-sm">{formatViewedAt(e.viewed_at)}</div>
                </div>
                <div className="col-span-12 md:col-span-3">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Viewer</div>
                  <div className="text-sm font-semibold flex items-center gap-1.5">
                    <Eye size={12} weight="bold" className="klein-blue" />
                    {e.viewer_name || e.viewer_email}
                  </div>
                  <div className="text-[11px] font-mono-plex text-neutral-500 truncate">{e.viewer_email}</div>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Auction</div>
                  <div className="text-sm font-semibold truncate">{e.auction_title || "—"}</div>
                  <div className="text-[11px] text-neutral-500 truncate">
                    by {e.auction_creator_name || "—"} · R{e.current_round_at_view ?? "?"} · {e.auction_status}
                  </div>
                </div>
                <div className="col-span-6 md:col-span-1 text-center">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 mb-1">Bids</div>
                  <div className="font-mono-plex text-lg">{e.bid_count_at_view ?? 0}</div>
                </div>
                <div className="col-span-6 md:col-span-1 flex md:justify-end">
                  {e.auction_id && (
                    <Link
                      to={`/auctions/${e.auction_id}`}
                      className="text-xs uppercase tracking-[0.2em] font-semibold underline underline-offset-4 flex items-center gap-1"
                      data-testid={`peek-log-open-auction-${e.auction_id}`}
                    >
                      Open <ArrowRight size={12} weight="bold" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-between border-t border-black/10 pt-6" data-testid="peek-log-pagination">
            <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="rounded-none border border-black/30 h-9 px-4 text-[11px] tracking-[0.25em] uppercase font-semibold btn-sharp disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="peek-log-prev"
              >
                Prev
              </button>
              <span className="text-xs font-mono-plex px-2">Page {page} / {totalPages}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="rounded-none border border-black/30 h-9 px-4 text-[11px] tracking-[0.25em] uppercase font-semibold btn-sharp disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="peek-log-next"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
